<template>
    <div class="pdf-converter-app">
        <div class="tool-left">
            <div class="converter-container">
                <h1 class="main-title">📄 PDF 转 Word</h1>
                <p class="subtitle">将您的 PDF 文件精准转换为可编辑的 Word 文档</p>

                <div class="upload-section">
                    <label for="pdf-upload" class="file-upload-label">
                        <div class="upload-icon">📁</div>
                        <div class="upload-text">点击选择或拖拽 PDF 文件</div>
                        <div class="upload-hint">支持 .pdf 格式</div>
                        <input
                                id="pdf-upload"
                                type="file"
                                @change="handleFileUpload"
                                accept=".pdf"
                                class="file-input"
                        />
                    </label>
                </div>

                <div v-if="pdfFileName" class="selected-file">
                    <span class="file-name">已选择: {{ pdfFileName }}</span>
                    <button @click="clearFile" class="clear-btn">清除</button>
                </div>

                <div class="action-section">
                    <button
                            @click="convertPdfToWord"
                            :disabled="!pdfFile || isProcessing"
                            class="convert-button"
                    >
                        <span v-if="!isProcessing">🚀 开始转换</span>
                        <span v-else>⏳ 转换中...</span>
                    </button>
                </div>

                <div v-if="statusMessage" :class="['status-message', { error: isError }]">
                    {{ statusMessage }}
                </div>

                <!-- 下载链接 -->
                <div v-if="wordFileUrl" class="download-section">
                    <a :href="wordFileUrl" download="converted_document.docx" class="download-link">
                        💾 点击下载转换后的 Word 文件
                    </a>
                </div>
            </div>
        </div>

        <!-- 内容说明区 -->
        <div class="tool-content">
            <h2>📄 免费 PDF 转 Word 在线工具</h2>

            <div class="tool-content-intro">
                <p>无需安装软件，浏览器直接完成 PDF 到 Word 的转换。支持文字、图片、表格识别，保留原始排版。</p>
            </div>

            <div class="content-section">
                <h3><span class="icon">✨</span> 功能特点</h3>
                <ul>
                    <li><strong>完全免费</strong>：无次数限制，无需注册登录</li>
                    <li><strong>隐私安全</strong>：文件在浏览器本地处理，不上传服务器</li>
                    <li><strong>保留格式</strong>：尽可能还原原文档的字体、颜色、布局</li>
                    <li><strong>批量转换</strong>：支持多页 PDF 一次性转换</li>
                </ul>
            </div>

            <div class="content-section">
                <h3><span class="icon">📝</span> 使用教程</h3>
                <ol>
                    <li>点击「选择文件」上传 PDF 文档</li>
                    <li>系统自动解析文档内容</li>
                    <li>点击「下载 Word」保存 .docx 文件</li>
                </ol>
            </div>

            <div class="content-section">
                <h3><span class="icon">❓</span> 常见问题</h3>
                <div class="faq-grid">
                    <div class="faq-item">
                        <p class="faq-q">转换后的 Word 格式会乱吗？</p>
                        <p class="faq-a">简单排版的文档转换效果最佳。复杂表格、特殊字体可能略有偏差。</p>
                    </div>
                    <div class="faq-item">
                        <p class="faq-q">文件大小有限制吗？</p>
                        <p class="faq-a">建议 20MB 以内，过大文件可能导致浏览器卡顿。</p>
                    </div>
                </div>
            </div>

            <div class="privacy-box">
                <span class="icon">🔒</span>
                <p><strong>隐私说明</strong>：本工具使用 pdf.js 和 docxtemplater 技术在您的浏览器本地完成转换，PDF 内容不会上传到任何服务器，请放心使用。</p>
            </div>
        </div>
    </div>
</template>

<script>
    import seoMixin from '../js/seo-mixin';
    import * as pdfjsLib from 'pdfjs-dist';
    import Docxtemplater from 'docxtemplater';
    import PizZip from 'pizzip';

    // --- 注意: workerSrc 需要指向正确的路径 ---
    // 在实际项目中，确保 pdf.worker.min.js 文件可以通过此路径访问
    // 例如，将其放置在 public 目录下，并使用相对路径 '/pdf.worker.min.js'
    pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js'; // <-- 请根据实际情况修改路径

    export default {
        name: "PdfToWordConverter",
        mixins: [seoMixin],
        data() {
            return {
                seoTitle: 'PDF转Word免费在线转换器 - FreeToolBox',
                seoDescription: '免费在线PDF转Word转换工具，浏览器本地处理，无需上传，保护隐私。支持批量转换，即下即用，无需注册登录。',
                seoKeywords: 'pdf转word,pdf转换word,pdf转docx,免费pdf转换,pdf在线转换',
                pdfFile: null,
                pdfFileName: '',
                wordFileUrl: null,
                isProcessing: false,
                statusMessage: '',
                isError: false,
            };
        },
        methods: {
            handleFileUpload(event) {
                const file = event.target.files[0];
                if (file && file.type === 'application/pdf') {
                    this.pdfFile = file;
                    this.pdfFileName = file.name;
                    this.wordFileUrl = null;
                    this.resetStatus();
                } else {
                    this.showStatus('请选择一个有效的 PDF 文件', true);
                }
                // 清空input，允许重复选择同一文件
                event.target.value = '';
            },
            clearFile() {
                this.pdfFile = null;
                this.pdfFileName = '';
                this.wordFileUrl = null;
                this.resetStatus();
            },
            async convertPdfToWord() {
                if (!this.pdfFile) {
                    this.showStatus('请先上传一个 PDF 文件', true);
                    return;
                }

                this.isProcessing = true;
                this.resetStatus();
                this.wordFileUrl = null; // 重置之前的下载链接

                try {
                    // --- 保持您原有的核心转换逻辑 ---
                    const reader = new FileReader();
                    reader.onload = async (e) => {
                        try {
                            const arrayBuffer = e.target.result;
                            const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
                            const pdf = await loadingTask.promise;

                            let textContent = '';
                            const tableData = [];

                            for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
                                const page = await pdf.getPage(pageNumber);
                                const content = await page.getTextContent();

                                let row = [];
                                content.items.forEach((item, index) => {
                                    textContent += item.str + ' ';

                                    // 简单的表格假设逻辑 (与原逻辑一致)
                                    if (index % 3 === 0 && row.length > 0) {
                                        tableData.push(row);
                                        row = [];
                                    }
                                    row.push(item.str.trim());
                                });

                                if (row.length > 0) {
                                    tableData.push(row);
                                }
                            }

                            // 加载模板文件
                            fetch('/template.docx') // <-- 确保 public 目录下有 template.docx
                                .then((response) => {
                                    if (!response.ok) {
                                        throw new Error(`模板加载失败: ${response.statusText}`);
                                    }
                                    return response.arrayBuffer();
                                })
                                .then((arrayBuffer) => {
                                    const zip = new PizZip(arrayBuffer);
                                    const doc = new Docxtemplater(zip, {
                                        paragraphLoop: true,
                                        linebreaks: true,
                                    });

                                    doc.setData({
                                        content: textContent,
                                        tableData: tableData,
                                    });

                                    try {
                                        doc.render();
                                    } catch (error) {
                                        console.error('Docxtemplater render error:', error);
                                        const e = {
                                            message: error.message,
                                            name: error.name,
                                            stack: error.stack,
                                            properties: error.properties,
                                        };
                                        throw new Error(`文档渲染失败: ${JSON.stringify({ ...e, message: e.message })}`);
                                    }

                                    const out = doc.getZip().generate({
                                        type: 'blob',
                                        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                                    });

                                    this.wordFileUrl = URL.createObjectURL(out);
                                    this.showStatus('转换完成！', false);
                                })
                                .catch((error) => {
                                    console.error('Template/Error:', error);
                                    this.showStatus(`转换失败: ${error.message || '未知错误'}`, true);
                                })
                                .finally(() => {
                                    this.isProcessing = false;
                                });
                        } catch (innerError) {
                            console.error("Conversion process error:", innerError);
                            this.showStatus(`转换失败: ${innerError.message || '未知错误'}`, true);
                            this.isProcessing = false;
                        }
                    };
                    reader.readAsArrayBuffer(this.pdfFile);
                    // --- 原有逻辑结束 ---
                } catch (error) {
                    console.error("File reading error:", error);
                    this.showStatus(`文件读取出错: ${error.message || '未知错误'}`, true);
                    this.isProcessing = false;
                }
            },
            showStatus(message, isError = false) {
                this.statusMessage = message;
                this.isError = isError;
            },
            resetStatus() {
                this.statusMessage = '';
                this.isError = false;
            }
        }
    };
</script>

<style scoped>
    .pdf-converter-app {
        min-height: 100vh;
        background: linear-gradient(135deg, #e0f7fa, #bbdefb);
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: 20px;
        gap: 40px;
        box-sizing: border-box;
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }

    .tool-left {
        flex: 1;
        display: flex;
        justify-content: center;
        max-width: 55%;
    }

    .converter-container {
        background-color: #ffffff;
        border-radius: 20px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        padding: 40px;
        width: 100%;
        max-width: 600px;
        text-align: center;
    }

    .main-title {
        font-size: 2.5em;
        color: #1e88e5;
        margin-bottom: 10px;
    }

    .subtitle {
        color: #666;
        font-size: 1.1em;
        margin-bottom: 30px;
    }

    .upload-section {
        margin-bottom: 30px;
    }

    .file-upload-label {
        display: block;
        border: 3px dashed #1e88e5;
        border-radius: 15px;
        padding: 40px 20px;
        cursor: pointer;
        transition: all 0.3s ease;
        background-color: #f5fbff;
        position: relative;
        overflow: hidden;
    }

    .file-upload-label:hover {
        background-color: #e3f2fd;
        border-color: #0d47a1;
    }

    .file-input {
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        opacity: 0;
        cursor: pointer;
    }

    .upload-icon {
        font-size: 3em;
        margin-bottom: 15px;
    }

    .upload-text {
        font-size: 1.2em;
        color: #333;
        margin-bottom: 10px;
    }

    .upload-hint {
        font-size: 0.9em;
        color: #888;
    }

    .selected-file {
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: #e8f5e9;
        padding: 12px 20px;
        border-radius: 10px;
        margin-bottom: 20px;
        box-sizing: border-box;
    }

    .file-name {
        font-weight: 500;
        color: #2e7d32;
        flex-grow: 1;
        text-align: left;
        margin-right: 10px;
        word-break: break-all;
        font-size: 0.95em;
    }

    .clear-btn {
        background: none;
        border: 1px solid #f44336;
        color: #f44336;
        padding: 5px 10px;
        border-radius: 5px;
        cursor: pointer;
        transition: background-color 0.2s;
        font-size: 0.9em;
    }

    .clear-btn:hover {
        background-color: #ffebee;
    }

    .action-section {
        margin: 30px 0;
    }

    .convert-button {
        font-size: 1.1em;
        padding: 12px 30px;
        border-radius: 50px;
        background: linear-gradient(90deg, #2196f3, #21cbf3);
        border: none;
        color: white;
        cursor: pointer;
        transition: transform 0.2s, box-shadow 0.2s;
        width: 100%;
        max-width: 300px;
    }

    .convert-button:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(33, 150, 243, 0.4);
    }

    .convert-button:disabled {
        background: #cccccc;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
    }

    .status-message {
        margin: 20px auto;
        padding: 15px;
        border-radius: 10px;
        font-weight: 500;
        max-width: 90%;
        word-break: break-word;
    }

    .status-message:not(.error) {
        background-color: #e3f2fd;
        color: #1976d2;
        border: 1px solid #90caf9;
    }

    .status-message.error {
        background-color: #ffebee;
        color: #c62828;
        border: 1px solid #ffcdd2;
    }

    .download-section {
        margin-top: 20px;
    }

    .download-link {
        display: inline-block;
        padding: 12px 25px;
        background: linear-gradient(90deg, #4caf50, #8bc34a);
        color: white;
        text-decoration: none;
        border-radius: 50px;
        font-weight: 500;
        transition: transform 0.2s, box-shadow 0.2s;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    }

    .download-link:hover {
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(76, 175, 80, 0.4);
    }
    /* 内容说明区样式 */
    .tool-content {
        flex: 1;
        max-width: 45%;
        margin: 0;
        padding: 0 20px;
        text-align: left;
    }

    .tool-content h2 {
        font-size: 1.8em;
        color: #333;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .tool-content h2::before {
        content: '';
        display: inline-block;
        width: 5px;
        height: 32px;
        background: linear-gradient(180deg, #4caf50, #8bc34a);
        border-radius: 3px;
    }

    .tool-content-intro {
        background: linear-gradient(135deg, #f0fdf0 0%, #e8f5e8 100%);
        border-radius: 16px;
        padding: 25px 30px;
        margin-bottom: 30px;
        border: 1px solid #c8e6c9;
        box-shadow: 0 4px 15px rgba(76, 175, 80, 0.08);
    }

    .tool-content-intro p {
        color: #2e7d32;
        font-size: 15px;
        line-height: 1.9;
        margin: 0;
    }

    .content-section {
        background: #fff;
        border-radius: 16px;
        padding: 25px 30px;
        margin-bottom: 20px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
        border: 1px solid #f0f0f0;
        transition: box-shadow 0.3s ease;
    }

    .content-section:hover {
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    }

    .content-section h3 {
        color: #333;
        font-size: 1.15em;
        margin: 0 0 15px 0;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .content-section h3 .icon {
        font-size: 1.2em;
    }

    .content-section ul, .content-section ol {
        margin: 0;
        padding-left: 24px;
    }

    .content-section li {
        color: #555;
        line-height: 2;
        margin-bottom: 8px;
        position: relative;
    }

    .content-section ul li::marker {
        color: #4caf50;
    }

    .content-section ol li::marker {
        color: #4caf50;
        font-weight: 600;
    }

    .content-section strong {
        color: #333;
    }

    .faq-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 15px;
    }

    .faq-item {
        background: #fafbfc;
        border-radius: 12px;
        padding: 18px 20px;
        border: 1px solid #eee;
    }

    .faq-item p {
        margin: 0;
    }

    .faq-q {
        font-weight: 600;
        color: #333;
        margin-bottom: 8px !important;
        display: flex;
        align-items: flex-start;
        gap: 8px;
    }

    .faq-q::before {
        content: 'Q';
        background: #4caf50;
        color: #fff;
        font-size: 12px;
        padding: 2px 8px;
        border-radius: 4px;
        flex-shrink: 0;
    }

    .faq-a {
        color: #666;
        font-size: 14px;
        line-height: 1.7;
        padding-left: 28px;
    }

    .privacy-box {
        background: linear-gradient(135deg, #f0f9f0 0%, #e8f5e8 100%);
        border: 1px solid #c8e6c9;
        border-radius: 12px;
        padding: 18px 25px;
        display: flex;
        align-items: flex-start;
        gap: 12px;
    }

    .privacy-box .icon {
        font-size: 1.5em;
        flex-shrink: 0;
    }

    .privacy-box p {
        margin: 0;
        color: #2e7d32;
        line-height: 1.7;
    }
</style>



